<template>
	<v-app>
		<div class="safe-area-top-fill primary"></div>

		<navbar @drawer="drawer = true" :loading="navbarLoading" v-if="authenticated && !loading && !hideChrome"></navbar>
		<navigation-drawer v-model="drawer" v-if="authenticated && !hideChrome"></navigation-drawer>

		<v-main>
			<v-alert
				v-if="isOffline && authenticated && !loading"
				dense
				text
				border="left"
				type="warning"
				icon="mdi-wifi-off"
				class="ma-2 mb-0 py-1 offline-banner"
				@click.native="offlineBannerExpanded = !offlineBannerExpanded"
			>
				<div class="d-flex align-center offline-banner__row">
					<span
						class="text-caption offline-banner__text"
						:class="{ 'offline-banner__text--expanded': offlineBannerExpanded }"
					>
						{{$T('offline-banner')}}
					</span>
					<v-icon small class="ms-1">
						{{ offlineBannerExpanded ? 'mdi-chevron-up' : 'mdi-chevron-down' }}
					</v-icon>
				</div>
			</v-alert>

			<router-view
				v-if="(authenticated || $route.name === 'login') && !loading"
				@loading:active="navbarLoading = true"
				@loading:disable="navbarLoading = false"
			/>
			<loader height="100%" v-if="(!authenticated && $route.name !== 'login') || loading"></loader>
		</v-main>
		<balance-banner v-if="false && authenticated && !loading && !hideChrome"></balance-banner>
		<footer-navigation v-if="authenticated && !loading && !hideChrome"></footer-navigation>

		<v-snackbar
			v-model="snackbar"
			app
			rounded="lg"
			elevation="4"
			transition="slide-y-reverse-transition"
			:color="stateColor"
			:timeout="5000"
		>
			<div class="d-flex align-center">
				<v-icon color="white" class="me-2">{{ stateIcon }}</v-icon>
				<span class="text-body-2">{{ message }}</span>
			</div>

			<template v-slot:action="{ attrs }">
				<v-btn
					icon
					color="white"
					v-bind="attrs"
					@click="snackbar = false"
				>
					<v-icon small>mdi-close</v-icon>
				</v-btn>
			</template>
		</v-snackbar>

		<v-snackbar
			v-model="updateAvailable"
			app
			rounded="lg"
			elevation="4"
			transition="slide-y-reverse-transition"
			color="primary"
			:timeout="-1"
		>
			<div class="d-flex align-center">
				<v-icon color="white" class="me-2">mdi-cloud-download-outline</v-icon>
				<span class="text-body-2">{{$T('app-update.title')}}</span>
			</div>

			<template v-slot:action="{ attrs }">
				<v-btn
					text
					color="white"
					v-bind="attrs"
					@click="refreshApp"
				>
					{{$T('app-update.action')}}
				</v-btn>
			</template>
		</v-snackbar>
	</v-app>
</template>

<script>
import Navbar from './components/Navbar'
import NavigationDrawer from './components/NavigationDrawer'
import BalanceBanner from './components/BalanceBanner'
import FooterNavigation from './components/FooterNavigation'
import Loader from './components/Loader'
import { mapState } from 'vuex'
import { useNotifications, usePrices } from './configs/general.configs'
import { isOffline } from './helpers/offline-simulation'
export default {
	name: 'App',

	data() {
		return {
			loading: false,
			drawer: false,
			navbarLoading: false,

			snackbar: false,
			messageState: false,
			message: null,
			updateAvailable: false,

			isOffline: isOffline(),
			offlineBannerExpanded: false,
		}
	},

	watch: {
		'$route.name' () {
			this.navbarLoading = false;
			this.$store.dispatch('configurations/fetchAll');
			this.$store.dispatch('configurations/fetchSystemConfigValue');
			this.fetchNotifications();
		},
		'$i18n.locale'(val) {
			this.loading = true;
			setTimeout(() => {
				this.loading = false;
			}, 500);
			this.$vuetify.lang.current = val;
			this.$vuetify.rtl = val === 'ar';
		},
		authenticated (val) {
			console.log('authenticated', val)
			// if (val) {
			this.fetchData()
			// }
			this.fetchNotifications()
		}
	},

	computed: {
		...mapState({
			authenticated: state => state.auth.authenticated
		}),

		hideChrome() {
			return Boolean(this.$route.meta?.hideChrome)
		},

		stateColor() {
			switch (this.messageState) {
			case 'success': return 'success';
			case 'error': return 'error';
			case 'warning': return 'warning';
			default: return 'info';
			}
		},

		stateIcon() {
			switch (this.messageState) {
			case 'success': return 'mdi-check-circle';
			case 'error': return 'mdi-alert-circle';
			case 'warning': return 'mdi-alert';
			default: return 'mdi-information';
			}
		}
	},

	methods: {
		fetchNotifications() {
			if (useNotifications && this.authenticated) {
				this.$store.dispatch('notifications/fetchUnreadCount');
			}
		},

		fetchData () {
			this.$store.dispatch('sync/init')
			this.$store.dispatch('vacations/fetchAll')
			this.$store.dispatch('entities/fetchAllGroupedByRegion')
			this.$store.dispatch('entities/fetchEntityType').then(() => {
				this.$store.dispatch('configurations/fetchEnum').then(() => {
					this.$store.dispatch('configurations/fetchAll')
				})
			})
			this.$store.dispatch('configurations/fetchSystemConfigValue')
			if (usePrices) {
				this.$store.dispatch('profile/fetchBalance')
			}
		},

		handleOnline() {
			this.isOffline = isOffline();
		},

		handleOffline() {
			this.isOffline = isOffline();
		},

		handleOfflineSimulationChanged() {
			this.isOffline = isOffline();
		},

		handleUpdateAvailable() {
			this.updateAvailable = true;
		},

		refreshApp() {
			window.location.reload();
		}
	},

	components: {
		Navbar,
		NavigationDrawer,
		BalanceBanner,
		FooterNavigation,
		Loader
	},

	created() {
		this.fetchData()
		this.fetchNotifications()
		this.$eventBus.$on('show-snackbar', (message, messageState) => {
			this.message = message;
			this.messageState = messageState || 'success';
			this.snackbar = true;
		})
		// تحديث الجرس/العدّاد عند وصول إشعار foreground
		this.$eventBus.$on('FCM-message', () => this.fetchNotifications())
		this.$eventBus.$on('offline-simulation-changed', this.handleOfflineSimulationChanged)
		this.$eventBus.$on('app-update-available', this.handleUpdateAvailable)
	},

	mounted() {
		window.addEventListener('online', this.handleOnline)
		window.addEventListener('offline', this.handleOffline)
	},

	beforeDestroy() {
		window.removeEventListener('online', this.handleOnline)
		window.removeEventListener('offline', this.handleOffline)
		this.$eventBus.$off('offline-simulation-changed', this.handleOfflineSimulationChanged)
		this.$eventBus.$off('app-update-available', this.handleUpdateAvailable)
	}
}
</script>

<style lang="scss">
@import './scss/vuetify-overrides.scss';
@media screen and (min-width: 600px) {
	*::-webkit-scrollbar{
		height: 4px;
		width: 4px;
		background-color: #f8f8f8;
	}
	*::-webkit-scrollbar-thumb{
		background-color: #aaaa;
		border-radius: 10px;
	}
}
.safe-area-top-fill{
    position: fixed;
    top: 0;
    left: 0;
    right: 0;
    height: env(safe-area-inset-top, 0px);
    z-index: 5;
    pointer-events: none;
}
.v-application .v-app-bar.v-app-bar--fixed{
    top: env(safe-area-inset-top, 0px) !important;
}
.v-application .v-main{
    margin-top: env(safe-area-inset-top, 0px);
}
.beetronix{
    font-size: .8rem;
    font-weight: bolder;
}
.offline-banner{
    cursor: pointer;
    overflow: hidden;
}
.offline-banner .v-alert__wrapper{
    min-width: 0;
}
.offline-banner .v-alert__content{
    min-width: 0;
    overflow: hidden;
}
.offline-banner__row{
    min-width: 0;
}
.offline-banner__text{
    min-width: 0;
    white-space: nowrap;
    overflow: hidden;
    text-overflow: ellipsis;
    flex: 1 1 auto;
}
.offline-banner__text--expanded{
    white-space: normal;
}
</style>
